/**
 * LinkBubble - キャレットがリンク内にある間、リンクの下に URL と「開く」ボタンを出すポップオーバー。
 * エディタではリンクをクリックしてもキャレットが置かれるだけなので、ここから URL を新しいタブで開く。
 */
import { flip, offset, shift } from '@floating-ui/react'
import type { Editor } from '@tiptap/react'
import { ExternalLink } from 'lucide-react'
import { useEffect, useState } from 'react'

import { type CaretLink, getCaretLink, toOpenableUrl } from '@/components/shared/link-bubble-utils'
import { useFloatingElement } from '@/hooks/use-floating-element'
import { useTiptapEditor } from '@/hooks/use-tiptap-editor'

const FLOATING_OPTIONS = {
  placement: 'bottom-start' as const,
  middleware: [offset(6), flip(), shift({ padding: 8 })],
  dismissOptions: { enabled: false },
}

function useCaretLink(editor: Editor | null): CaretLink | null {
  const [link, setLink] = useState<CaretLink | null>(null)

  useEffect(() => {
    if (!editor) return
    const update = () => {
      const next = editor.isFocused ? getCaretLink(editor) : null
      setLink((prev) => (prev?.href === next?.href && prev?.element === next?.element ? prev : next))
    }
    update()
    editor.on('transaction', update)
    editor.on('focus', update)
    editor.on('blur', update)
    return () => {
      editor.off('transaction', update)
      editor.off('focus', update)
      editor.off('blur', update)
    }
  }, [editor])

  return link
}

export function LinkBubble({ editor: providedEditor }: { editor?: Editor | null }) {
  const { editor } = useTiptapEditor(providedEditor)
  const link = useCaretLink(editor)
  const url = link ? toOpenableUrl(link.href) : null
  const { isMounted, ref, style, getFloatingProps } = useFloatingElement(
    Boolean(link),
    link?.element ?? null,
    50,
    FLOATING_OPTIONS,
  )

  if (!link || !isMounted) return null

  const open = () => {
    if (!url) return
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <div
      ref={ref}
      style={style}
      {...getFloatingProps()}
      role="dialog"
      aria-label="リンク"
      className="flex max-w-[min(24rem,calc(100vw-1rem))] items-center gap-1 rounded-md border bg-popover py-1 pr-1 pl-3 text-sm text-popover-foreground shadow-md"
      // エディタのフォーカス（=キャレット位置）を保ったまま操作できるようにする
      onMouseDown={(event) => event.preventDefault()}
    >
      <span className="min-w-0 flex-1 truncate text-muted-foreground" title={link.href}>
        {link.href}
      </span>
      <button
        type="button"
        onClick={open}
        disabled={!url}
        className="flex shrink-0 items-center gap-1 rounded px-2 py-1 font-medium hover:bg-accent disabled:opacity-50"
      >
        <ExternalLink className="size-4" />
        開く
      </button>
    </div>
  )
}
